import React, { useState, useEffect, Fragment } from "react";
import {
  Row,
  Col,
  Button,
  Input,
  Label,
  FormGroup,
  CustomInput,
} from "reactstrap";
import NumberFormat from "react-number-format";
import TextareaAutosize from "react-textarea-autosize";
import ApiCaller from "../../../../api/apiCaller";
import "./index.css";
import t from "utils/Lang";

interface SiteCommentBoxInputProps {
  siteId: any;
  onCommentSaved?: Function;
  onCancelClicked?: Function;
  comment?: any;
  disableEditSitePerformanceEstimation?: Boolean;
  sitePerformanceEstimationFields?: Array<any>;
}

const SiteCommentBox = ({
  siteId,
  onCommentSaved,
  onCancelClicked,
  comment,
  disableEditSitePerformanceEstimation,
  sitePerformanceEstimationFields,
}: SiteCommentBoxInputProps) => {
  const randomString = Math.random().toString(36).substring(7);
  const fileInputId = `${Date.now().toString()}-${randomString}`;

  const [commentData, setCommentData] = useState<any>({
    site_id: parseInt(comment?.site_id || siteId, 10),
    ...comment,
  });
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [showPerformanceEstimation, setShowPerformanceEstimation] =
    useState<boolean>(
      commentData.personal_site_revenue_per_month_estimation_min ||
        commentData.personal_site_revenue_per_month_estimation_max ||
        commentData.personal_site_revenue_per_month_estimation_avg ||
        commentData.personal_site_ebitda_per_month_estimation_min ||
        commentData.personal_site_ebitda_per_month_estimation_max ||
        commentData.personal_site_ebitda_per_month_estimation_avg ||
        false
    );
  const [attachmentToUploads, setAttachmentToUploads] =
    useState<Array<any> | null>([]);

  const textAreaChangeHandler = (event: any) => {
    setCommentData({ ...commentData, content: event.target.value });
  };

  const sitePerformanceEstimationHandler = (
    event: any,
    attributeName: string
  ) => {
    const newCommentData = { ...commentData };
    let valueData = event.target.value;
    if (valueData.includes(",")) {
      valueData = valueData.replace(/,/g, "");
    }
    newCommentData[attributeName] = valueData;
    setCommentData(newCommentData);
  };

  const attachFileButtonClick = () => {
    if (document.getElementById(fileInputId)) {
      document?.getElementById(fileInputId)?.click();
    }
  };

  const selectImagesToUpload = (event: any) => {
    const filesArray = Array.from(event.target.files);
    const newAttachmentToUploads = filesArray.map((file: any) => {
      return {
        blob: file,
        url: URL.createObjectURL(file),
      };
    });
    if (attachmentToUploads) {
      setAttachmentToUploads([
        ...attachmentToUploads,
        ...newAttachmentToUploads,
      ]);
    } else {
      setAttachmentToUploads([...newAttachmentToUploads]);
    }
  };

  const deleteAttachmentToUpload = (url: string) => {
    if (attachmentToUploads) {
      setAttachmentToUploads(
        attachmentToUploads.filter((attachment) => attachment.url != url)
      );
    }
  };

  const deleteExistingAttachment = async (attachmentId: any) => {
    if (!attachmentId) alert("Lỗi: không tìm thấy attachment_id");
    const confirmationBox = window.confirm("Bạn có chắc sẽ xóa hình này?");
    if (!confirmationBox) return;
    const uri = `file_attachment/${attachmentId}/delete`;
    const res = await ApiCaller.delete(uri);
    if (res.status === "ok") {
      const currentCommentData = commentData;
      setCommentData({
        ...commentData,
        attachments: currentCommentData.attachments.filter(
          (attachment: any) => attachment.id != attachmentId
        ),
      });
    } else {
      alert(res.msg);
    }
  };

  const saveComment = async () => {
    let uri = "";
    let res = null;
    setIsSaving(true);
    let commentId = null;
    let data: any = {};
    Object.keys(commentData)?.map((k) => {
      if (commentData[k]) {
        data[k] = commentData[k];
      }
    });
    if (commentData?.id) {
      commentId = commentData.id;
      // Update mode
      uri = `site_comment/${commentData.id}`;
      res = await ApiCaller.put(uri, data);
    } else {
      // Create mode
      uri = `site_comment/`;
      res = await ApiCaller.post(uri, data);
      if (res.id) {
        commentId = res.id;
      }
    }
    if (res?.result) {
      if (attachmentToUploads && attachmentToUploads.length > 0) {
        await uploadAttachments(commentId);
        setAttachmentToUploads(null);
      }
      setIsSaving(false);
      if (onCommentSaved) {
        const uri = `site_comment/list?id=${commentId}`;
        const res = await ApiCaller.get(uri);
        if (res?.result) {
          onCommentSaved(res?.result[0]);
        }
      }
      const newCommentData = {
        ...commentData,
        content: "",
      };
      sitePerformanceEstimationFields?.forEach((fieldGroup: any) => {
        fieldGroup.items.forEach((field: any) => {
          newCommentData[field.code] = "";
        });
      });
      setCommentData(newCommentData);
    }
  };

  const uploadAttachments = async (siteCommentId: any) => {
    let promiseAll: Array<any> = [];
    if (attachmentToUploads) {
      attachmentToUploads.forEach(async (attachment) => {
        var attachmentFormData = new FormData();
        attachmentFormData.append("user_file", attachment.blob);
        const uri = `site_comment/${siteCommentId}/attachments`;
        promiseAll.push(ApiCaller.post(uri, attachmentFormData));
      });
      const resAttachmentData = await Promise.all(promiseAll);
      console.log(resAttachmentData);
    }
  };

  const cancelEditComment = () => {
    if (onCancelClicked) {
      onCancelClicked();
    }
  };

  useEffect(() => {}, []);

  return (
    <div style={styles.commentBoxContainer}>
      <div>
        <TextareaAutosize
          type="textarea"
          minRows={2}
          placeholder={t().site_comment__comment_placeholder}
          onChange={textAreaChangeHandler}
          value={commentData.content}
          style={styles.commentBox}
          autoFocus
        />
        <div style={styles.performanceEstimation}>
          <FormGroup>
            <CustomInput
              id={`showSitePerformanceEstimation-comment-${
                commentData.id || 0
              }`}
              type="switch"
              label={t().site_comment__estimate_site_revenue}
              onChange={(e) =>
                setShowPerformanceEstimation(!showPerformanceEstimation)
              }
              checked={showPerformanceEstimation}
            />
          </FormGroup>
        </div>
        {!disableEditSitePerformanceEstimation && showPerformanceEstimation && (
          <div>
            {sitePerformanceEstimationFields
              ?.filter((fieldGroup) => fieldGroup.can_input)
              .map((fieldGroup) => (
                <Fragment key={fieldGroup.name}>
                  <div>
                    <b>{fieldGroup.name}</b>
                  </div>
                  <Row>
                    {fieldGroup.items.map((field: any) => (
                      <Col xs={12} sm={4} key={field.code}>
                        <FormGroup>
                          <Label style={styles.fieldName}>{field.name}</Label>
                          <NumberFormat
                            className="form-control"
                            thousandSeparator={true}
                            value={commentData[field.code]}
                            onChange={(e) =>
                              sitePerformanceEstimationHandler(e, field.code)
                            }
                          />
                        </FormGroup>
                      </Col>
                    ))}
                  </Row>
                </Fragment>
              ))}
          </div>
        )}
        <div style={styles.imageContainer}>
          {commentData?.attachments?.map((attachment: any, index: number) => (
            <Fragment key={`${attachment.id}`}>
              {attachment.file_type === "image" ? (
                <div
                  style={{
                    backgroundImage: `url(${attachment.file_url})`,
                    ...styles.imageAttachment,
                  }}
                >
                  <button
                    style={styles.imageAttachmentDeleteButton}
                    onClick={() => deleteExistingAttachment(attachment.id)}
                  >
                    X
                  </button>
                </div>
              ) : (
                <div style={styles.fileAttachment}>
                  <span>{attachment.file_name}</span>
                  <button
                    style={styles.generalFileAttachmentDeleteButton}
                    onClick={() => deleteExistingAttachment(attachment.id)}
                  >
                    X
                  </button>
                </div>
              )}
            </Fragment>
          ))}
          {attachmentToUploads && (
            <Fragment>
              {attachmentToUploads.map((attachment, index) => (
                <Fragment key={`attachment-${index}`}>
                  {attachment?.blob?.type.includes("image") ? (
                    <div
                      style={{
                        backgroundImage: `url(${attachment.url})`,
                        ...styles.imageAttachment,
                      }}
                    >
                      <button
                        style={styles.imageAttachmentDeleteButton}
                        onClick={() => deleteAttachmentToUpload(attachment.url)}
                      >
                        X
                      </button>
                    </div>
                  ) : (
                    <div style={styles.fileAttachment}>
                      <span>{attachment?.blob?.name}</span>
                      <button
                        style={styles.generalFileAttachmentDeleteButton}
                        onClick={() => deleteAttachmentToUpload(attachment.id)}
                      >
                        X
                      </button>
                    </div>
                  )}
                </Fragment>
              ))}
            </Fragment>
          )}
        </div>
      </div>
      <div style={styles.commentButtonContainer}>
        <div>
          <Button
            outline
            size="sm"
            color="primary"
            onClick={attachFileButtonClick}
            style={styles.commentButton}
          >
            {t().site_photo_tab__insert_photo}
          </Button>
          <Input
            type="file"
            label={t().site_photo_tab__select_file}
            title=""
            id={fileInputId}
            multiple
            hidden
            style={styles.selectFileButton}
            onChange={(e) => selectImagesToUpload(e)}
            key={fileInputId}
          />
        </div>
        {commentData.id && (
          <Button
            size="sm"
            color="secondary"
            onClick={cancelEditComment}
            style={styles.commentButton}
          >
            {t().action__cancel}
          </Button>
        )}
        <Button
          size="sm"
          color={isSaving || !commentData.content ? "secondary" : "primary"}
          disabled={isSaving || !commentData.content}
          onClick={saveComment}
          style={styles.commentButton}
        >
          {isSaving
            ? t().action__posting
            : commentData.id
            ? t().action__save
            : t().action__post}
        </Button>
      </div>
    </div>
  );
};

const styles = {
  commentBoxContainer: {
    backgroundColor: "var(--color-line-light)",
    borderRadius: 6,
  } as React.CSSProperties,
  performanceEstimation: {
    marginTop: 6,
  },
  commentBox: {
    backgroundColor: "var(--color-white)",
    border: "none",
    width: "100%",
    padding: 12,
    fontSize: 16,
  },
  commentButtonContainer: {
    backgroundColor: "var(--color-line-light)",
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-end",
    marginTop: 6,
    paddingRight: 3,
    paddingBottom: 3,
  } as React.CSSProperties,
  commentButton: {
    marginLeft: 6,
  },
  fieldName: {
    fontSize: 12,
  },
  selectFileButton: {},
  icon: {
    fontSize: 36,
    color: "var(--color-white)",
  },
  imageContainer: {
    display: "flex",
    flexWrap: "wrap",
    marginTop: 12,
    marginBottom: 12,
  } as React.CSSProperties,
  imageAttachment: {
    width: 90,
    height: 90,
    borderRadius: 6,
    marginRight: 12,
    marginBottom: 12,
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    backgroundSize: "cover",
    position: "relative",
    backgroundColor: "var(--color-line)",
    border: "1px solid var(--color-line)",
  } as React.CSSProperties,
  imageAttachmentDeleteButton: {
    fontSize: 12,
    width: 20,
    height: 20,
    borderRadius: "50%",
    border: "none",
    backgroundColor: "var(--color-muted)",
    color: "var(--color-white)",
    position: "absolute",
    top: 4,
    right: 4,
  } as React.CSSProperties,
  generalFileAttachmentDeleteButton: {
    fontSize: 12,
    width: 20,
    height: 20,
    borderRadius: "50%",
    border: "none",
    backgroundColor: "var(--color-muted)",
    color: "var(--color-white)",
    marginLeft: 16,
  },
  fileAttachment: {
    marginRight: 12,
    fontSize: 12,
    fontWeight: 500,
    borderRadius: 3,
    padding: "6px 12px",
    backgroundColor: "var(--color-line)",
  },
};

export default SiteCommentBox;
